import { Box, Button, Chip, FormControl, InputLabel, MenuItem, Pagination, Paper, Select, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useLang } from '../../../../language';
import { InputText } from '../../../../components/Inputs/InputText';
import { ModalStatusServer } from '../../../../components/ModalStatus/ModalStatusServer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBan, faCalendarCheck, faCheckCircle, faUsers } from '@fortawesome/free-solid-svg-icons';
import { InscripcionesServices } from '../../controllers/services/inscripciones.services';
import { ActividadesServices } from '../../controllers/services/actividades.services';

/**
 * "Mis actividades": listado de actividades asignadas a la empresa (accion
 * `listar_inscripciones`, una fila por actividad con el total de inscritos) y,
 * al pulsar el botón de conteo, el listado de personas inscritas en esa
 * actividad (accion `listar_inscripciones_personas`), con filtro por estado
 * (opciones de `estados_incripcion`) y botón de Anular por persona.
 */
export const MisActividades = ({ setBreadcrumbLineItems, route, empresa }: any) => {

    const Lang = useLang()

    const { empresaId } = useParams()

    // CIF/NIF de la empresa (del detalle que ya carga EmpresaDetalle), que el WS
    // usa para localizar el cliente (igual que en inscribir_personas_actividad).
    const cif = empresa?.cif || ''

    // --- Listado de actividades asignadas ---
    const [filtroActividad, setFiltroActividad] = useState('')
    const [filtroAplicado, setFiltroAplicado] = useState('')
    const [paginacionActividades, setPaginacionActividades] = useState({ page: 1, last_page: 1 })

    // --- Inscritos de la actividad seleccionada ---
    const [actividadSeleccionada, setActividadSeleccionada] = useState<any>(null)
    const [estadoFiltro, setEstadoFiltro] = useState('')
    const [paginacionPersonas, setPaginacionPersonas] = useState({ page: 1, last_page: 1 })

    // Se incrementa tras anular una inscripción para refrescar la lista
    const [refreshKey, setRefreshKey] = useState(0)

    const [fetchActividadesGET, fetchActividadesGETData] = InscripcionesServices.LISTAR_INSCRIPCIONES()

    const [fetchPersonasGET, fetchPersonasGETData] = InscripcionesServices.LISTAR_INSCRIPCIONES_PERSONAS()

    // Estados de inscripción para el filtro (accion=estados_incripcion)
    const [fetchEstadosGET, fetchEstadosGETData] = InscripcionesServices.GET_ESTADOS_INSCRIPCION()

    const [fetchAnularPOST, fetchAnularPOSTData] = ActividadesServices.ANULAR_INSCRIPCION()

    const actividades = fetchActividadesGETData.data?.items || []

    const personas = fetchPersonasGETData.data?.items || []

    const estados = fetchEstadosGETData.data?.estados || []

    // Loading inmediato: `loading` empieza en `null` (la primera petición espera
    // al CIF de la empresa). Tratamos null como "cargando" para que el skeleton
    // aparezca al instante y no haya un hueco en blanco.
    const cargandoActividades = fetchActividadesGETData.loading !== false

    const cargandoPersonas = fetchPersonasGETData.loading !== false

    const errorActividades = fetchActividadesGETData.error

    const errorPersonas = fetchPersonasGETData.error

    // Una inscripción anulada tiene in_estado '109' (no se puede volver a anular)
    const esAnulada = (item: any) => String(item.in_estado) === '109'

    // Etiqueta del estado: se prefiere el catálogo de `estados_incripcion`
    // (código -> texto traducido), con fallback al texto que traiga la fila.
    const estadoLabel = (item: any) =>
        estados.find((e: any) => e.value === String(item.in_estado))?.label
        || item.estado
        || item.in_estado
        || ''

    useEffect(() => {

        setBreadcrumbLineItems((item: any) => [...item.slice(0, 2), route])

    }, [])

    // Catálogo de estados (se carga una vez al montar)
    useEffect(() => {

        fetchEstadosGET({})

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Búsqueda por actividad con debounce (solo afecta al listado de actividades)
    useEffect(() => {

        const handler = setTimeout(() => {

            setFiltroAplicado(filtroActividad.trim())

            setPaginacionActividades((prev: any) => ({ ...prev, page: 1 }))

        }, 400)

        return () => clearTimeout(handler)

    }, [filtroActividad])

    // Listado de actividades asignadas (espera al CIF de la empresa)
    useEffect(() => {

        if (!empresaId || !cif) return

        fetchActividadesGET({
            empresa_id: Number(empresaId),
            cif,
            actividad: filtroAplicado,
            page: paginacionActividades.page,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresaId, cif, filtroAplicado, paginacionActividades.page])

    // Inscritos de la actividad seleccionada (con filtro por estado)
    useEffect(() => {

        if (!empresaId || !cif || !actividadSeleccionada?.actividadId) return

        fetchPersonasGET({
            empresa_id: Number(empresaId),
            cif,
            actividad_id: actividadSeleccionada.actividadId,
            estado: estadoFiltro,
            page: paginacionPersonas.page,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresaId, cif, actividadSeleccionada?.actividadId, estadoFiltro, paginacionPersonas.page, refreshKey])

    // Sincroniza la página seleccionada con la respuesta del WS (actividades)
    useEffect(() => {

        const paginacion = fetchActividadesGETData.data?.paginacion

        if (paginacion?.page != null) {

            setPaginacionActividades({
                page: Number(paginacion.page),
                last_page: Number(paginacion.last_page || paginacion.page),
            })
        }

    }, [fetchActividadesGETData.data])

    // Sincroniza la página seleccionada con la respuesta del WS (personas)
    useEffect(() => {

        const paginacion = fetchPersonasGETData.data?.paginacion

        if (paginacion?.page != null) {

            setPaginacionPersonas({
                page: Number(paginacion.page),
                last_page: Number(paginacion.last_page || paginacion.page),
            })
        }

    }, [fetchPersonasGETData.data])

    // Tras anular una inscripción se refresca la lista de inscritos (y el total)
    useEffect(() => {

        if (String(fetchAnularPOSTData.status)?.[0] === '2') {

            setRefreshKey((key: number) => key + 1)
        }

    }, [fetchAnularPOSTData])

    const handleVerInscritos = (actividad: any) => {

        setActividadSeleccionada(actividad)

        setPaginacionPersonas((prev: any) => ({ ...prev, page: 1 }))
    }

    const handleVolver = () => {

        setActividadSeleccionada(null)

        setPaginacionPersonas((prev: any) => ({ ...prev, page: 1 }))
    }

    const handleCambioPaginaActividades = (event: any, nuevaPagina: number) => {

        setPaginacionActividades((prev: any) => ({ ...prev, page: nuevaPagina }))
    }

    const handleCambioPaginaPersonas = (event: any, nuevaPagina: number) => {

        setPaginacionPersonas((prev: any) => ({ ...prev, page: nuevaPagina }))
    }

    const handleCambioEstado = (event: any) => {

        setEstadoFiltro(event.target.value)

        setPaginacionPersonas((prev: any) => ({ ...prev, page: 1 }))
    }

    const handleAnular = (item: any) => {

        if (!item?.id) return

        // POST {WS}/integraWeb.php  accion=anular_inscripcion (body JSON)
        fetchAnularPOST({ id: item.id })
    }

    return (
        <div>

            <Paper className='p-3 p-md-4' sx={{ borderRadius: 2.5 }}>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} className='mb-2'>
                    <FontAwesomeIcon icon={faCalendarCheck} className='color-primary' />
                    <Typography variant='h6' className='my-0 fw-500 color-primary'>
                        {Lang('MIS_ACTIVIDADES')}
                    </Typography>
                </Box>

                {
                    !actividadSeleccionada &&
                    <Box className='mb-2'>
                        <InputText
                            label={Lang('BUSCAR_ACTIVIDAD')}
                            name='buscarActividad'
                            value={filtroActividad}
                            onChange={(e: any) => setFiltroActividad(e.target.value)}
                        />
                    </Box>
                }

            </Paper>

            {
                !actividadSeleccionada &&

                <Paper className='p-3 p-md-4 mt-3'>

                    <Typography variant='h6' className="my-0 pb-1 fw-500 color-primary">
                        {Lang('ACTIVIDADES_ASIGNADAS')}
                    </Typography>

                    {
                        !cargandoActividades && errorActividades &&
                        <Typography variant='caption' className='my-0 mb-2' sx={{ color: 'error.main', display: 'block', textAlign: 'center' }}>
                            {String(errorActividades?.message || errorActividades)}
                        </Typography>
                    }

                    <TableContainer className='mt-3'>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{Lang('ACTIVIDAD')}</TableCell>
                                    <TableCell>{Lang('FECHA_INICIO')}</TableCell>
                                    <TableCell>{Lang('LLOC')}</TableCell>
                                    <TableCell align="right">{Lang('ACCIONES')}</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>

                                {
                                    cargandoActividades &&

                                    Array.from({ length: 5 }).map((_, index) => (
                                        <TableRow key={`skeleton-${index}`}>
                                            <TableCell><Skeleton variant="text" /></TableCell>
                                            <TableCell><Skeleton variant="text" width={100} /></TableCell>
                                            <TableCell><Skeleton variant="text" width={90} /></TableCell>
                                            <TableCell align="right" width={120}><Skeleton variant="rounded" height={32} /></TableCell>
                                        </TableRow>
                                    ))
                                }

                                {
                                    !cargandoActividades && actividades.map((item: any) => (
                                        <TableRow key={item.id || item.actividad}>
                                            <TableCell>{item.actividad}</TableCell>
                                            <TableCell>{item.finicio}</TableCell>
                                            <TableCell>{item.lugar}</TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    startIcon={<FontAwesomeIcon icon={faUsers} style={{ fontSize: 11 }} />}
                                                    onClick={() => handleVerInscritos(item)}
                                                    title={Lang('ACTIVITAT_INSCRITOS')}
                                                >
                                                    {item.inscritos}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }

                                {
                                    !cargandoActividades && !errorActividades && actividades.length === 0 &&

                                    <TableRow>
                                        <TableCell colSpan={4}>
                                            <Box sx={{ textAlign: 'center', py: 5 }}>
                                                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                                    {Lang('SENSE_INSCRIPCIONES')}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                }

                            </TableBody>
                        </Table>
                    </TableContainer>

                    {
                        fetchActividadesGETData.loading !== null && (actividades.length > 0 || fetchActividadesGETData.loading === true) &&
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant='caption' className='my-0' sx={{ color: 'text.secondary' }}>
                                {paginacionActividades.page} de {paginacionActividades.last_page}
                            </Typography>
                            {
                                paginacionActividades.last_page > 1 &&
                                <Pagination
                                    page={paginacionActividades.page}
                                    count={paginacionActividades.last_page}
                                    onChange={handleCambioPaginaActividades}
                                    color="primary"
                                    size="small"
                                />
                            }
                        </Box>
                    }

                </Paper>
            }

            {
                actividadSeleccionada &&

                <Paper className='p-3 p-md-4 mt-3'>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant='h6' className="my-0 fw-500 color-primary">
                            {Lang('ACTIVITAT_INSCRITOS')}
                            {
                                actividadSeleccionada.actividad &&
                                <Typography component='span' variant='body2' sx={{ color: 'text.secondary', ml: 1 }}>
                                    — {actividadSeleccionada.actividad}
                                </Typography>
                            }
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 230 }}>
                                <InputLabel id="select-estado-inscripcion">{Lang('ESTADO')}</InputLabel>
                                <Select
                                    labelId="select-estado-inscripcion"
                                    label={Lang('ESTADO')}
                                    value={estadoFiltro}
                                    onChange={handleCambioEstado}
                                >
                                    <MenuItem value="">{Lang('TODOS_ESTADOS')}</MenuItem>
                                    {
                                        estados.map((e: any) => (
                                            <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>

                            <Button
                                size="small"
                                color="inherit"
                                variant="outlined"
                                startIcon={<FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 11 }} />}
                                onClick={handleVolver}
                            >
                                {Lang('VOLVER')}
                            </Button>
                        </Box>
                    </Box>

                    {
                        !cargandoPersonas && errorPersonas &&
                        <Typography variant='caption' className='my-0 mt-2' sx={{ color: 'error.main', display: 'block', textAlign: 'center' }}>
                            {String(errorPersonas?.message || errorPersonas)}
                        </Typography>
                    }

                    <TableContainer className='mt-2'>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{Lang('PERSONA')}</TableCell>
                                    <TableCell>{Lang('NIF')}</TableCell>
                                    <TableCell>{Lang('ESTADO')}</TableCell>
                                    <TableCell>{Lang('PORCENTAJE_ASISTENCIA')}</TableCell>
                                    <TableCell>{Lang('PRECIO')}</TableCell>
                                    <TableCell align="right">{Lang('ACCIONES')}</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>

                                {
                                    cargandoPersonas &&

                                    Array.from({ length: 5 }).map((_, index) => (
                                        <TableRow key={`skeleton-${index}`}>
                                            <TableCell><Skeleton variant="text" /></TableCell>
                                            <TableCell><Skeleton variant="text" width={110} /></TableCell>
                                            <TableCell width={140}><Skeleton variant="rounded" height={28} /></TableCell>
                                            <TableCell width={110}><Skeleton variant="text" /></TableCell>
                                            <TableCell width={80}><Skeleton variant="text" /></TableCell>
                                            <TableCell align="right" width={140}><Skeleton variant="rounded" height={32} /></TableCell>
                                        </TableRow>
                                    ))
                                }

                                {
                                    !cargandoPersonas && personas.map((item: any) => (
                                        <TableRow key={item.id || `${item.persona}-${item.nif}`}>
                                            <TableCell>{item.persona}</TableCell>
                                            <TableCell>{item.nif}</TableCell>
                                            <TableCell>
                                                {
                                                    estadoLabel(item) &&
                                                    <Chip
                                                        label={estadoLabel(item)}
                                                        size="small"
                                                        color={esAnulada(item) ? 'default' : 'primary'}
                                                        variant={esAnulada(item) ? 'filled' : 'outlined'}
                                                    />
                                                }
                                            </TableCell>
                                            <TableCell>{item.asistencia}</TableCell>
                                            <TableCell>{item.precio}</TableCell>
                                            <TableCell align="right">
                                                {
                                                    !esAnulada(item) &&
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        startIcon={<FontAwesomeIcon icon={faBan} style={{ fontSize: 11 }} />}
                                                        onClick={() => handleAnular(item)}
                                                    >
                                                        {Lang('ANULAR_INSCRIPCION')}
                                                    </Button>
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }

                                {
                                    !cargandoPersonas && !errorPersonas && personas.length === 0 &&

                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Box sx={{ textAlign: 'center', py: 5 }}>
                                                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                                    {Lang('SENSE_INSCRIPCIONES')}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                }

                            </TableBody>
                        </Table>
                    </TableContainer>

                    {
                        fetchPersonasGETData.loading !== null && (personas.length > 0 || fetchPersonasGETData.loading === true) &&
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant='caption' className='my-0' sx={{ color: 'text.secondary' }}>
                                {paginacionPersonas.page} de {paginacionPersonas.last_page}
                            </Typography>
                            {
                                paginacionPersonas.last_page > 1 &&
                                <Pagination
                                    page={paginacionPersonas.page}
                                    count={paginacionPersonas.last_page}
                                    onChange={handleCambioPaginaPersonas}
                                    color="primary"
                                    size="small"
                                />
                            }
                        </Box>
                    }

                </Paper>
            }

            <ModalStatusServer
                fetchData={fetchAnularPOSTData}
                activeLoading
                messageStatus={
                    {
                        200:
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className='color-green fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchAnularPOSTData.data?.message || Lang('INSCRIPCION_ANULADA_OK')}
                                </Typography>
                            </>,
                        201:
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} className='color-green fa-2x' />

                                <Typography sx={{ mt: 2 }}>
                                    {fetchAnularPOSTData.data?.message || Lang('INSCRIPCION_ANULADA_OK')}
                                </Typography>
                            </>,
                    }
                }
            />

        </div>
    )
}